import { Card, Grid, Skeleton, Tooltip, Typography } from '@mui/material';
import {
  BaseCardContainer,
  BaseCardText,
  OverviewDataContainer,
  ResultDataContainer,
  ScenarioCards,
  ScenarioCardsContainer,
  ScenarioOverviewContainer,
  ScenarioOverviewLabel,
} from './ScenarioOverview.Styled';
import CustomButton from '../../../../components/UI/CustomButton/CustomButton';
import {
  AddToCompare,
  OutdatedIcon,
  ScenarioRemove,
  ScenarioShare,
  ScenarioUpdate,
} from '../../../../assets/images/UI-dashboard';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import {
  removeScenario,
  selectOutputOverviewData,
  selectOutputOverviewError,
  selectOutputOverviewLoading,
  setLatestScenarioNames,
  setOutputScreen,
} from '../../../../features/outputOverview/outputOverviewSlice';
import { formatNumber, truncateName } from '../../../../utils/helper';
import { useNavigate } from 'react-router-dom';
import { SegmentPackDeepDive } from '../SegmentPackDeepDive/SegmentPackDeepDive';
import ErrorPopup from '../../../../components/ErrorPopup/ErrorPopup';
import { useEffect, useState } from 'react';
import {
  fetchSegmentDeepDive,
  selectIsDeepdiveVisible,
  setDeepdiveVisible,
  setScenarios,
  setSelectedScenario,
} from '../../../../features/outputSegmentDeepDive/outputSegmentDeepDiveSlice';
import { hideLoader, showLoader } from '../../../../features/ui/loaderSlice';
import { RootState } from '../../../../state/store';
import { SCENARIO_OUTPUT_DATA } from '../../../../utils/dataConstants';
import { fetchOutputBrands } from '../../../../features/outputSegmentDeepDive/outputBrandsSlice';
import ConfirmationDialog from '../../../../components/UI/ConfirmationDialog';
import { resetForm } from '../../../../components/FormComponent/formSlice';
import {
  fetchEditScenarioData,
  fetchUserData,
  openShareDialog,
  setComingFromScreen,
  setEditScenarioFlag,
  setScenariosForComparison,
  updatedSelectedUserShareList,
  updateSelectedCategory,
  updateSelectedStatus,
} from '../../../../features/scenarioLibrary/scenarioLibrarySlice';
import {
  resetScenarioName,
  setScenarioAction,
  setScenarioName,
} from '../../../../features/scenarioCreation/scenarioCreationSlice';
import {
  resetReviewTable,
  toggleEnableReviewTableBtn,
  toggleEnableReviewTableDialog,
} from '../../../ScenarioCreation/components/ReviewTable/reviewTableSlice';
import { resetPerformanceTableState } from '../../../../features/performanceTable/performanceTableSlice';
import { resetInputKpiState } from '../../../../features/inputKpi/inputKpiSlice';
import { SelectMapperKeys, ViewByMapperKeysForDeepdive } from '../../../../types/common';
import ShareToUserPopup from '../../../ScenarioLibrary/components/ShareToUserPopup';
import { CONSTANTS } from '../../../../utils/constant';

interface BaseScenario {
  total_transactions_sum: number;
  total_volume_sum: number;
  base_market_share: number;
  net_revenue_base: number;
  nr_per_uc: number;
  volume_pc: number;
}

const commonGridStyles = { display: 'flex', alignContent: 'center', alignItems: 'center', justifyContent: 'center' };
const commonFontStyles = { fontSize: '12.71px', fontWeight: 600, lineHeight: '15.49px' };

export const ScenarioOverview = () => {
  const dispatch = useAppDispatch();
  const overviewData = useAppSelector(selectOutputOverviewData);
  const isDeepdiveVisible = useAppSelector(selectIsDeepdiveVisible);
  const { selectedToggle, selectedViewBy, enabledKeys } = useAppSelector((state: RootState) => state.outputFilters);
  const { overviewState } = useAppSelector((state: RootState) => state.outputOverview);
  const { scenarios, selectedScenarios } = useAppSelector((state: RootState) => state.outputSegmentDeepDive);

  const isLoading = useAppSelector(selectOutputOverviewLoading);
  const error = useAppSelector(selectOutputOverviewError);
  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [selectedScenarioName, setSelectedScenarioName] = useState<string | undefined>(undefined);
  const [deleteScenarioName, setDeleteScenarioName] = useState('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const staticOverviewData = SCENARIO_OUTPUT_DATA.STATIC_OVERVIEW_DATA;

  const baseRGMMapper = (() => {
    const baseMapper = SCENARIO_OUTPUT_DATA.BASE_RGM_MAPPER || {};
    if (selectedToggle !== 'All Segment') {
      const { base_market_share, ...rest } = baseMapper;
      return rest;
    }
    return baseMapper;
  })();
  const baseFinanceMapper = SCENARIO_OUTPUT_DATA.BASE_FINANCE_MAPPER || {};

  const scenarioRGMMapper = (() => {
    const baseMapper = SCENARIO_OUTPUT_DATA.SCENARIO_RGM_MAPPER || {};
    if (selectedToggle !== 'All Segment') {
      const { scenario_market_share, ...rest } = baseMapper;
      return rest;
    }
    return baseMapper;
  })();

  const scenarioFinanceMapper = SCENARIO_OUTPUT_DATA.SCENARIO_FINANCE_MAPPER || {};

  const scenarioCardsData = overviewData ? overviewData.slice(1) : staticOverviewData.slice(1);
  const baseScenario: BaseScenario = overviewData
    ? overviewData[0]?.base || {
        total_transactions_sum: 0,
        total_volume_sum: 0,
        base_market_share: 0,
        net_revenue_base: 0,
        nr_per_uc: 0,
        volume_pc: 0,
      }
    : staticOverviewData[0]?.base || {
        total_transactions_sum: 0,
        total_volume_sum: 0,
        base_market_share: 0,
        net_revenue_base: 0,
        nr_per_uc: 0,
        volume_pc: 0,
      };
  const navigate = useNavigate();

  const AddScenariosToCompare = async () => {
    if (scenarioCardsData.length >= 5) {
      setErrorPopupOpen(true);
    } else {
      await dispatch(setOutputScreen(true));
      await dispatch(setComingFromScreen('scenarioOutput'));
      const scenariosForComparison: string[] = [];
      scenarioCardsData.forEach((ele: any) => {
        scenariosForComparison.push(ele.scenario_id);
      });
      await dispatch(setScenariosForComparison(scenariosForComparison));
      await dispatch(updateSelectedCategory(['Core Sparkling']));
      await dispatch(updateSelectedStatus(['Completed']));
      await dispatch(setDeepdiveVisible(false));
      navigate(`/${CONSTANTS.PATH.SCENARIOLIBRARY}`);
    }
  };

  const handleCloseErrorPopup = () => {
    setErrorPopupOpen(false);
  };

  const handleRemoveScenario = (scenarioName: string) => {
    setDeleteScenarioName(scenarioName);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    await dispatch(removeScenario(deleteScenarioName));

    const updatedScenarios: string[] = scenarioCardsData
      .filter((ele: any) => ele.scenario_name !== deleteScenarioName)
      .map((ele: any) => ele.scenario_name);

    const data = selectedScenarios;
    if (data.base === deleteScenarioName) {
      await dispatch(setDeepdiveVisible(false));
    } else if (data.child === deleteScenarioName) {
      await dispatch(setSelectedScenario({ type: 'child', scenario: selectedScenarios.base }));
    }

    await dispatch(setScenarios(updatedScenarios));
    await dispatch(setLatestScenarioNames(updatedScenarios));
    await setOpenDialog(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleShareScenario = async (name: string) => {
    await dispatch(showLoader());
    await dispatch(setScenarioName(name));
    await dispatch(fetchUserData());
    await dispatch(dispatch(updatedSelectedUserShareList([])));
    await dispatch(hideLoader());
    await dispatch(openShareDialog());
  };

  const handleUpdateScenario = async (name: string) => {
    navigate(`/${CONSTANTS.PATH.SCENARIOCREATION}`);
    await dispatch(showLoader());
    await dispatch(resetForm());
    await dispatch(setEditScenarioFlag(true));
    await dispatch(setScenarioName(name));
    await dispatch(fetchEditScenarioData({ scenario_name: name }));
    await dispatch(toggleEnableReviewTableDialog(true));
    await dispatch(setScenarioAction('refresh'));
    await dispatch(hideLoader());
  };

  const createNewScenario = async () => {
    await dispatch(resetScenarioName());
    await dispatch(resetPerformanceTableState());
    await dispatch(resetInputKpiState());
    await dispatch(toggleEnableReviewTableBtn(false));
    await dispatch(toggleEnableReviewTableDialog(false));
    await dispatch(resetReviewTable());
    await dispatch(resetForm());
    await dispatch(setScenarioAction('new'));
    navigate(`/${CONSTANTS.PATH.SCENARIOCREATION}`);
  };

  useEffect(() => {
    if (selectedScenarioName === '' || selectedScenarioName === undefined) {
      dispatch(setDeepdiveVisible(false));
    }
  }, [selectedScenarioName]);

  type Result = {
    valueOne: string;
    valueTwo: string;
  };

  const getValidValues = (array: string[], valueOne: string): Result => {
    if (!array.includes(valueOne)) {
      throw new Error(`valueOne (${valueOne}) is not in the provided array.`);
    }

    let valueTwoOptions = array.filter((item) => item !== valueOne);

    if (array.length === 1) {
      return { valueOne: array[0], valueTwo: array[0] };
    }
    return {
      valueOne,
      valueTwo: valueTwoOptions.length > 0 ? valueTwoOptions[0] : '',
    };
  };

  const selectedScenarioToDeepdive = async (name: string) => {
    if (name === selectedScenarioName) {
      setSelectedScenarioName('');
      await dispatch(setDeepdiveVisible(false));
    } else {
      setSelectedScenarioName(name);
      const base_scenario = name;

      const child_scenario =
        selectedScenarios.child === '' ? getValidValues(scenarios, name).valueTwo : selectedScenarios.child;

      await dispatch(showLoader());
      if (name) {
        await dispatch(setSelectedScenario({ type: 'base', scenario: base_scenario }));
        await dispatch(setSelectedScenario({ type: 'child', scenario: child_scenario }));
        const outputBrandPayload = {
          scenario_name: scenarios,
          bottler: enabledKeys.bottler,
          state: enabledKeys.state,
        };
        const response = await dispatch(fetchOutputBrands(outputBrandPayload));
        const segmentPayload = {
          scenario_1: base_scenario,
          scenario_2: child_scenario,
          brand: response.payload,
          bottler: enabledKeys.bottler,
          state: enabledKeys.state,
          select: SCENARIO_OUTPUT_DATA.SELECT_MAPPER[selectedToggle as SelectMapperKeys],
          view_by: SCENARIO_OUTPUT_DATA.VIEW_BY_MAPPER_DEEPDIVE[selectedViewBy as ViewByMapperKeysForDeepdive],
        };
        await dispatch(fetchSegmentDeepDive(segmentPayload));
        await dispatch(setDeepdiveVisible(true));
        await dispatch(hideLoader());
      }
    }
  };

  return (
    <ResultDataContainer container>
      <ScenarioOverviewContainer>
        <ScenarioOverviewLabel>Scenario Overview</ScenarioOverviewLabel>
        <OverviewDataContainer container>
          <Grid>
            <Card
              style={{
                width: '271.43px',
                height: selectedToggle === 'All Segment' ? '480px' : '450px',
                border: '0.91px solid #E5E5E5',
              }}
            >
              <BaseCardContainer>
                <Grid
                  style={{
                    ...commonGridStyles,
                  }}
                >
                  <Typography
                    style={{
                      ...commonFontStyles,
                    }}
                  ></Typography>
                </Grid>
                <Grid
                  style={{
                    ...commonGridStyles,
                  }}
                >
                  <Typography
                    style={{
                      ...commonFontStyles,
                    }}
                  >
                    KPI’s
                  </Typography>
                </Grid>
                <Grid
                  style={{
                    ...commonGridStyles,
                    justifyContent: 'end',
                    marginRight: '20%',
                  }}
                >
                  <Typography
                    style={{
                      ...commonFontStyles,
                    }}
                  >
                    Base
                  </Typography>
                </Grid>
              </BaseCardContainer>
              <Grid
                style={{
                  alignContent: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {overviewData === null ? (
                  <Grid style={{ height: '100%' }}>
                    <Skeleton animation="wave" variant="rectangular" width={280} height={430} />
                  </Grid>
                ) : (
                  <>
                    <Grid
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }}
                    >
                      <Grid
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '20% 80%',
                        }}
                      >
                        <Grid
                          style={{
                            justifyContent: 'center',
                            alignContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <BaseCardText
                            style={{
                              transform: 'rotate(-90deg)',
                              fontWeight: 600,
                            }}
                          >
                            RGM
                          </BaseCardText>
                        </Grid>
                        <Grid>
                          {Object.entries(baseRGMMapper).map(([key, label], i) => (
                            <Grid
                              key={i}
                              container
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '70% 30%',
                                marginBottom: '10px',
                                padding: '0px 20px',
                              }}
                            >
                              <BaseCardText
                                style={{
                                  marginTop: '7px',
                                  textAlign: 'left',
                                }}
                              >
                                {label}
                              </BaseCardText>
                              <BaseCardText
                                style={{
                                  marginTop: '7px',
                                  textAlign: 'right',
                                  fontWeight: '600',
                                }}
                              >
                                {formatNumber(
                                  key === 'base_market_share' ? baseScenario[key] * 100 : baseScenario[key],
                                )}
                              </BaseCardText>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                      <Grid
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '20% 80%',
                          background: '#FFF4F4',
                        }}
                      >
                        <Grid
                          style={{
                            justifyContent: 'center',
                            alignContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <BaseCardText
                            style={{
                              transform: 'rotate(-90deg)',
                              fontWeight: 600,
                            }}
                          >
                            Finance
                          </BaseCardText>
                        </Grid>
                        <Grid style={{ marginTop: '7px' }}>
                          {Object.entries(baseFinanceMapper).map(([key, label], i) => (
                            <Grid
                              key={i}
                              container
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '70% 30%',
                                marginBottom: '10px',
                                padding: '0px 20px',
                              }}
                            >
                              <BaseCardText
                                style={{
                                  marginTop: '6px',
                                }}
                              >
                                {label}
                              </BaseCardText>
                              <BaseCardText
                                style={{
                                  marginTop: '6px',
                                  textAlign: 'right',
                                  fontWeight: '600',
                                }}
                              >
                                {formatNumber(
                                  key === 'bottler_gp_percent' || key === 'ou_gp_percent' || key === 'system_gp_percent'
                                    ? baseScenario[key] * 100
                                    : baseScenario[key],
                                )}
                              </BaseCardText>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </Card>
          </Grid>

          <Grid>
            {overviewData === null ? (
              <ScenarioCardsContainer>
                {['1', '2', '3', '4', '5'].map(() => (
                  <ScenarioCards style={{ height: '100%', border: 'none' }}>
                    <Skeleton animation="wave" variant="rectangular" width={280} height={480} />
                  </ScenarioCards>
                ))}
              </ScenarioCardsContainer>
            ) : (
              <ScenarioCardsContainer>
                {scenarioCardsData.map((scenario, i) => (
                  <ScenarioCards
                    key={i}
                    style={{
                      width: '120px',
                      height: selectedToggle === 'All Segment' ? '480px' : '450px',
                      marginRight: '15px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      border:
                        selectedScenarioName === scenario.scenario_name ? '1px solid #E81C0E' : '1px solid #E5E5E5',
                    }}
                  >
                    <Grid
                      style={{
                        background: '#F7F8FB',
                        height: '46.3px',
                        ...commonGridStyles,
                        borderBottom:
                          selectedScenarioName === scenario.scenario_name ? '1px solid #E81C0E' : '1px solid #E5E5E5',
                        position: 'relative',
                      }}
                    >
                      {scenario.scenario_expired === true && (
                        <img style={{ position: 'absolute', top: 5, right: 5 }} src={OutdatedIcon} alt="outdated" />
                      )}
                      <Tooltip title={scenario.scenario_name} arrow>
                        <Typography
                          style={{
                            ...commonFontStyles,
                            cursor: 'pointer',
                          }}
                        >
                          {truncateName(scenario.scenario_name, 10)}
                        </Typography>
                      </Tooltip>
                      <Typography
                        style={{
                          color: '#E81C0E',
                          marginLeft: '2px',

                          fontSize: '9.08px',
                          lineHeight: '11.07px',
                          fontWeight: 'bolder',
                        }}
                      >
                        CON
                      </Typography>
                    </Grid>
                    <Grid
                      onClick={() => selectedScenarioToDeepdive(scenario.scenario_name)}
                      style={{ marginTop: '7px' }}
                    >
                      {Object.entries(scenarioRGMMapper).map(([key, label], i) => (
                        <Grid
                          key={i}
                          container
                          style={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignContent: 'start',
                            alignItems: 'start',
                            marginBottom:
                              selectedToggle !== 'All Segment' && key === 'nr_per_uc'
                                ? '10px'
                                : key === 'scenario_market_share'
                                ? '10px'
                                : '17px',
                            flexDirection: 'row',
                          }}
                        >
                          <BaseCardText
                            sx={{
                              color:
                                selectedViewBy === 'Incremental'
                                  ? scenario.result[key] > 0
                                    ? 'green'
                                    : 'red'
                                  : 'black',
                            }}
                          >
                            {formatNumber(
                              selectedViewBy === 'Percent'
                                ? scenario.result[key] * 100
                                : key === 'scenario_market_share'
                                ? scenario.result[key] * 100
                                : scenario.result[key],
                            )}
                          </BaseCardText>
                        </Grid>
                      ))}
                    </Grid>
                    <Grid style={{ paddingTop: '14px', background: '#FFF4F4' }}>
                      {Object.entries(scenarioFinanceMapper).map(([key, label], i) => (
                        <Grid
                          key={i}
                          container
                          style={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignContent: 'start',
                            alignItems: 'start',
                            // marginBottom: '16px',
                            marginBottom: key === 'system_gp_percent' ? '0px' : '16px',
                            flexDirection: 'row',
                          }}
                        >
                          <BaseCardText
                            style={{
                              marginBottom: key === 'system_gp_percent' ? '8px' : '0px',
                              color:
                                selectedViewBy === 'Incremental'
                                  ? scenario.result[key] > 0
                                    ? 'green'
                                    : 'red'
                                  : 'black',
                            }}
                          >
                            {formatNumber(
                              selectedViewBy === 'Percent'
                                ? scenario.result[key] * 100
                                : key === 'bottler_gp_percent' || key === 'ou_gp_percent' || key === 'system_gp_percent'
                                ? scenario.result[key] * 100
                                : scenario.result[key],
                            )}
                          </BaseCardText>
                        </Grid>
                      ))}
                    </Grid>
                    <div
                      style={{
                        borderTop:
                          selectedScenarioName === scenario.scenario_name ? '1px solid #E81C0E' : '1px solid #E5E5E5',
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'row',
                        paddingTop: '10px',
                        cursor: 'pointer',
                      }}
                    >
                      <Tooltip title={'Update Scenario'}>
                        <img
                          src={ScenarioUpdate}
                          style={{
                            opacity: scenario.scenario_expired ? 0.8 : 1,
                          }}
                          onClick={() => {
                            if (scenario.scenario_expired) {
                              handleUpdateScenario(scenario.scenario_name!);
                            }
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={'Share Scenario'}>
                        <img src={ScenarioShare} onClick={() => handleShareScenario(scenario.scenario_name!)} />
                      </Tooltip>
                      <Tooltip title={'Remove Scenario'}>
                        <img onClick={() => handleRemoveScenario(scenario.scenario_name!)} src={ScenarioRemove} />
                      </Tooltip>
                    </div>
                  </ScenarioCards>
                ))}
              </ScenarioCardsContainer>
            )}
          </Grid>

          {overviewData === null ? (
            <>
              <Grid style={{ height: '100%' }}>
                <Skeleton animation="wave" variant="rectangular" width={230} height={480} />
              </Grid>
            </>
          ) : (
            <>
              <Grid>
                <Card
                  style={{
                    width: '228.77px',
                    height: selectedToggle === 'All Segment' ? '480px' : '450px',
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    border: '0.91px solid #E5E5E5',
                    ...commonGridStyles,
                    flexDirection: 'column',
                  }}
                >
                  <img style={{ cursor: 'pointer' }} onClick={AddScenariosToCompare} src={AddToCompare} />
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: '10.89px',
                      lineHeight: '13.28px',
                      marginTop: '20px',
                    }}
                  >
                    Add Scenario to Compare
                  </Typography>
                  <Grid
                    style={{
                      marginTop: '20px',
                      width: '150px',
                      ...commonGridStyles,
                    }}
                  >
                    <Typography
                      style={{
                        fontWeight: 400,
                        fontSize: '9.08px',
                        lineHeight: '12.36px',
                        textAlign: 'center',
                      }}
                    >
                      Create or add an existing scenario to compare the output.
                    </Typography>
                  </Grid>

                  <CustomButton
                    style={{
                      marginTop: '50px',
                      width: '207.01px',
                      borderRadius: '19.9px',
                    }}
                    onClick={AddScenariosToCompare}
                  >
                    Select from Library
                  </CustomButton>

                  <Typography
                    style={{
                      marginTop: '10px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '9.08px',
                      lineHeight: '11.07px',
                      color: '#E81C0E',
                    }}
                    onClick={createNewScenario}
                  >
                    Create New
                  </Typography>
                </Card>
              </Grid>
            </>
          )}
        </OverviewDataContainer>
        {isDeepdiveVisible && <SegmentPackDeepDive />}
      </ScenarioOverviewContainer>
      <ShareToUserPopup />
      <ConfirmationDialog val="Remove" open={openDialog} onClose={handleCloseDialog} onConfirm={handleConfirmDelete} />
      <ErrorPopup
        open={errorPopupOpen}
        message={'Already five scenarios are present, please delete few before proceeding'}
        onClose={handleCloseErrorPopup}
      />
    </ResultDataContainer>
  );
};
